// src/pages/GalleryTransmission.jsx
// Parcours « continuité et transmission » d'une galerie (archives, collection propre, estate d'artiste) : huit étapes,
// chacune mesurée sur les données déjà saisies, avec un lien vers l'endroit où la compléter, puis les dossiers à remettre.
// Même principe que FoundationTransmission, avec le vocabulaire et les critères d'une galerie.
// ARTICHAUT organise l'information ; il ne donne ni conseil juridique ni conseil fiscal et ne qualifie aucun droit.
// Textes en français, anglais et allemand (langue de l'interface). Aucune donnée n'est écrite par cette page.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import SuccessionDossierButton from "../components/SuccessionDossierButton";
import ContinuityReportButton from "../components/ContinuityReportButton";
import EstatePiecesCard from "../components/EstatePiecesCard";

const TEXT = {
  fr: {
    title: "Continuité et transmission de la galerie",
    lead: "Huit étapes pour que les archives, les œuvres et leur histoire restent compréhensibles par ceux qui reprendront le relais. Chaque étape se mesure sur ce que vous avez déjà saisi.",
    back: "← Tableau de bord",
    loading: "Chargement…", none: "Aucune collection pour le moment.",
    worksOf: (a, b) => `${a} sur ${b}`,
    groupsOf: (a, b) => `${a} sur ${b} ensemble${b > 1 ? "s" : ""}`,
    fix: "Compléter", noWork: "Aucune œuvre à mesurer",
    steps: [
      ["Nature des ensembles", "Chaque ensemble a une nature indiquée : collection personnelle, collection de société, archives de galerie ou estate d'artiste."],
      ["Statut de détention", "Les œuvres dont on sait si elles appartiennent à la galerie, à une personne ou à une société, ou si elles sont en dépôt ou en consignation. « À clarifier » ne compte pas."],
      ["Propriétaire confirmé", "Les œuvres dont le propriétaire n'est pas signalé « à vérifier »."],
      ["Provenance et pièces", "Les œuvres qui ont à la fois une provenance et au moins un document."],
      ["Destination des œuvres", "Les œuvres pour lesquelles une destination est indiquée (garder, vendre, transmettre, donner…)."],
      ["Bénéficiaires désignés", "Les œuvres attribuées à au moins un bénéficiaire (héritier, repreneur, ayant droit)."],
      ["Accès accordés", "Les personnes ou liens qui peuvent consulter le dossier (notaire, conseil, repreneur…)."],
    ],
    accessCount: (n) => (n === 0 ? "Aucun accès accordé" : `${n} accès actif${n > 1 ? "s" : ""}`),
    dossiersTitle: "8. Les dossiers à remettre",
    dossiersHelp: "À compléter puis à faire valider par votre notaire ou votre conseil juridique. Pour un lien de partage sans valeurs ni prix, utilisez « Partager le dossier » sur la collection.",
    disclaimer: "ARTICHAUT organise l'information : il ne donne ni conseil juridique ni conseil fiscal, et ne remplace ni le notaire, ni l'avocat, ni l'expert. Les droits attachés à un estate relèvent des professionnels compétents.",
    notGallery: "Cette page est réservée aux comptes galerie. Pour l'activer sur votre compte, écrivez-nous depuis le formulaire de contact.",
  },
  en: {
    title: "Continuity and transmission of the gallery",
    lead: "Eight steps so that the archives, the artworks and their history remain understandable to those who will take over. Each step is measured on what you have already entered.",
    back: "← Dashboard",
    loading: "Loading…", none: "No collection yet.",
    worksOf: (a, b) => `${a} of ${b}`,
    groupsOf: (a, b) => `${a} of ${b} group${b > 1 ? "s" : ""}`,
    fix: "Complete", noWork: "No work to measure",
    steps: [
      ["Nature of the groups", "Each group has a stated nature: personal collection, company collection, gallery archive or artist estate."],
      ["Ownership status", "Works for which it is known whether they belong to the gallery, a person or a company, or are on deposit or consignment. “To clarify” does not count."],
      ["Owner confirmed", "Works whose owner is not flagged “to verify”."],
      ["Provenance and documents", "Works that have both a provenance and at least one document."],
      ["Destination of the works", "Works for which a destination is stated (keep, sell, transfer, donate…)."],
      ["Named beneficiaries", "Works allocated to at least one beneficiary (heir, successor, rights holder)."],
      ["Access granted", "People or links that can view the file (notary, adviser, successor…)."],
    ],
    accessCount: (n) => (n === 0 ? "No access granted" : `${n} active access${n > 1 ? "es" : ""}`),
    dossiersTitle: "8. The files to hand over",
    dossiersHelp: "To be completed, then validated by your notary or legal adviser. For a share link without values or prices, use “Share the file” on the collection.",
    disclaimer: "ARTICHAUT organises information: it gives neither legal nor tax advice, and replaces neither the notary, the lawyer nor the expert. The rights attached to an estate are for the competent professionals.",
    notGallery: "This page is reserved for gallery accounts. To enable it on your account, write to us through the contact form.",
  },
  de: {
    title: "Kontinuität und Übertragung der Galerie",
    lead: "Acht Schritte, damit Archive, Werke und ihre Geschichte für diejenigen verständlich bleiben, die übernehmen. Jeder Schritt wird anhand Ihrer bereits erfassten Angaben gemessen.",
    back: "← Dashboard",
    loading: "Wird geladen…", none: "Noch keine Sammlung.",
    worksOf: (a, b) => `${a} von ${b}`,
    groupsOf: (a, b) => `${a} von ${b} ${b > 1 ? "Beständen" : "Bestand"}`,
    fix: "Vervollständigen", noWork: "Kein Werk zu messen",
    steps: [
      ["Art der Bestände", "Jeder Bestand hat eine angegebene Art: persönliche Sammlung, Unternehmenssammlung, Galeriearchiv oder Künstlernachlass."],
      ["Eigentumsstatus", "Werke, bei denen bekannt ist, ob sie der Galerie, einer Person oder einer Gesellschaft gehören oder Leihgabe bzw. Kommission sind. „Zu klären“ zählt nicht."],
      ["Eigentümer bestätigt", "Werke, deren Eigentümer nicht als „zu prüfen“ markiert ist."],
      ["Provenienz und Belege", "Werke mit Provenienz und mindestens einem Dokument."],
      ["Verbleib der Werke", "Werke, für die ein Verbleib angegeben ist (behalten, verkaufen, übertragen, schenken …)."],
      ["Benannte Begünstigte", "Werke, die mindestens einem Begünstigten zugeordnet sind (Erbe, Nachfolger, Berechtigter)."],
      ["Gewährte Zugänge", "Personen oder Links, die die Akte einsehen können (Notar, Berater, Nachfolger …)."],
    ],
    accessCount: (n) => (n === 0 ? "Kein Zugang gewährt" : `${n} aktive${n > 1 ? " Zugänge" : "r Zugang"}`),
    dossiersTitle: "8. Die zu übergebenden Akten",
    dossiersHelp: "Zu vervollständigen und anschließend von Ihrem Notar oder Rechtsberater prüfen zu lassen. Für einen Freigabelink ohne Werte und Preise nutzen Sie „Dossier teilen“ in der Sammlung.",
    disclaimer: "ARTICHAUT ordnet Informationen: Es gibt weder Rechts- noch Steuerberatung und ersetzt weder den Notar noch den Anwalt noch den Gutachter. Die an einen Nachlass geknüpften Rechte obliegen den zuständigen Fachleuten.",
    notGallery: "Diese Seite ist Galeriekonten vorbehalten. Um sie für Ihr Konto freizuschalten, schreiben Sie uns über das Kontaktformular.",
  },
};

const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));
const hasText = (v) => v != null && String(v).trim() !== "";
const hasDocs = (a) => !(a?.documents == null || a.documents === "" || a.documents === "[]");
const detentionKnown = (a) => hasText(a.heritage_status) && a.heritage_status !== "to_clarify";

function tone(ok, total) {
  if (!total) return { bar: "#cfcac0", text: "#8a8378" };
  const r = ok / total;
  if (r >= 0.8) return { bar: "#2f6b3f", text: "#2f6b3f" };
  if (r > 0) return { bar: "#b9892f", text: "#8a6a1f" };
  return { bar: "#b4572f", text: "#b4572f" };
}

const ACCESS_STEP = 6; // index de l'étape « Accès accordés »

export default function GalleryTransmission() {
  const { user, profile } = useAuth();
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const L = TEXT[lang] || TEXT.fr;

  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!user?.id) return undefined;
    (async () => {
      const { data: cols } = await supabase
        .from("collections").select("id,title,ensemble_nature").eq("client_id", user.id).order("title");
      const own = cols || [];
      const ids = own.map((c) => c.id);
      let arts = [];
      if (ids.length) {
        const { data: a } = await supabase
          .from("artworks_secure")
          .select("id,collection_id,provenance,documents,destination,ownership_verification,heritage_status")
          .in("collection_id", ids).limit(5000);
        arts = a || [];
      }
      const withBen = new Set();
      for (const part of chunk(arts.map((a) => a.id), 200)) {
        const { data: links } = await supabase.from("artwork_beneficiary").select("artwork_id").in("artwork_id", part);
        (links || []).forEach((l) => withBen.add(l.artwork_id));
      }
      const { data: perms } = await supabase
        .from("collection_permissions").select("grantee_id,collection_id,can_view").eq("client_id", user.id).eq("can_view", true);
      const grants = (perms || []).filter((p) => p.grantee_id !== user.id && ids.includes(p.collection_id)).length;
      const { data: shares } = await supabase
        .from("dossier_shares").select("id").eq("client_id", user.id).is("revoked_at", null).gt("expires_at", new Date().toISOString());
      if (!alive) return;
      setData({ own, arts, withBen, access: grants + (shares?.length || 0) });
    })().catch(() => { if (alive) setData({ own: [], arts: [], withBen: new Set(), access: 0 }); });
    return () => { alive = false; };
  }, [user?.id]);

  const steps = useMemo(() => {
    if (!data) return [];
    const { own, arts, withBen, access } = data;
    const firstOwn = own[0]?.id;
    const firstWith = (pred) => (own.find((c) => arts.some((a) => a.collection_id === c.id && pred(a)))?.id) || firstOwn;
    const missingNature = own.find((c) => !hasText(c.ensemble_nature));
    return [
      { ok: own.filter((c) => hasText(c.ensemble_nature)).length, total: own.length, to: missingNature ? `/collections/${missingNature.id}/edit` : null, groups: true },
      { ok: arts.filter(detentionKnown).length, total: arts.length, to: firstWith((a) => !detentionKnown(a)) },
      { ok: arts.filter((a) => a.ownership_verification !== "to_verify").length, total: arts.length, to: firstWith((a) => a.ownership_verification === "to_verify") },
      { ok: arts.filter((a) => hasText(a.provenance) && hasDocs(a)).length, total: arts.length, to: firstWith((a) => !(hasText(a.provenance) && hasDocs(a))) },
      { ok: arts.filter((a) => hasText(a.destination)).length, total: arts.length, to: firstWith((a) => !hasText(a.destination)) },
      { ok: arts.filter((a) => withBen.has(a.id)).length, total: arts.length, to: firstWith((a) => !withBen.has(a.id)) },
      { access, to: firstOwn },
    ];
  }, [data]);

  // Page réservée aux comptes galerie (ou ayant déjà une collection « archives de galerie » / « estate d'artiste »).
  const isGalleryUser = profile?.gallery_mode === true
    || (data?.own || []).some((c) => c.ensemble_nature === "gallery_archive" || c.ensemble_nature === "artist_estate");

  const wrap = { maxWidth: 860, margin: "0 auto", padding: "32px 20px 64px", fontFamily: "system-ui, sans-serif", color: "#1a1a1a" };
  if (!data) return <div style={wrap}>{L.loading}</div>;

  const linkTo = (id) => (id ? `/collections/${id}` : null);

  if (!isGalleryUser) {
    return (
      <div style={wrap}>
        <Link to="/dashboard" style={{ fontSize: 13, color: "#8a8378" }}>{L.back}</Link>
        <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 30, margin: "10px 0 6px" }}>{L.title}</h1>
        <p style={{ color: "#555", fontSize: 14, lineHeight: 1.5 }}>{L.notGallery}</p>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <Link to="/dashboard" style={{ fontSize: 13, color: "#8a8378" }}>{L.back}</Link>
      <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 30, margin: "10px 0 6px" }}>{L.title}</h1>
      <p style={{ color: "#555", fontSize: 14, lineHeight: 1.5, margin: "0 0 24px" }}>{L.lead}</p>

      {data.own.length === 0 ? (
        <p style={{ color: "#8a8378" }}>{L.none}</p>
      ) : (
        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
          {L.steps.map(([name, hint], i) => {
            const s = steps[i] || {};
            const isAccess = i === ACCESS_STEP;
            const total = isAccess ? 1 : s.total;
            const ok = isAccess ? (s.access > 0 ? 1 : 0) : s.ok;
            const c = tone(ok, total);
            const target = s.groups ? s.to : linkTo(s.to);
            const complete = total > 0 && ok >= total;
            return (
              <li key={name} style={{ border: "1px solid #e5e3df", borderRadius: 8, background: "#fff", padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
                  <strong style={{ fontSize: 15 }}>{i + 1}. {name}</strong>
                  <span style={{ fontSize: 13, color: c.text, fontWeight: 600 }}>
                    {isAccess ? L.accessCount(s.access) : total ? (s.groups ? L.groupsOf(ok, total) : L.worksOf(ok, total)) : L.noWork}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: "#6f695f", margin: "4px 0 8px", lineHeight: 1.45 }}>{hint}</div>
                {!isAccess && total > 0 && (
                  <div style={{ height: 6, background: "#eee9df", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${Math.round((ok / total) * 100)}%`, height: "100%", background: c.bar }} />
                  </div>
                )}
                {!complete && target && (
                  <Link to={target} style={{ display: "inline-block", marginTop: 8, fontSize: 12.5, color: "#a9843f", fontWeight: 600 }}>{L.fix} →</Link>
                )}
              </li>
            );
          })}
          <li style={{ border: "1px solid #e5e3df", borderRadius: 8, background: "#fff", padding: "14px 16px" }}>
            <strong style={{ fontSize: 15 }}>{L.dossiersTitle}</strong>
            <div style={{ fontSize: 12.5, color: "#6f695f", margin: "4px 0 10px" }}>{L.dossiersHelp}</div>
            <div style={{ display: "grid", gap: 8 }}>
              {data.own.map((col) => (
                <div key={col.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, minWidth: 160 }}>{col.title}</span>
                  <SuccessionDossierButton collectionId={col.id} collectionName={col.title || ""} />
                  <ContinuityReportButton collectionId={col.id} ownerName={col.title || ""} />
                </div>
              ))}
            </div>
          </li>
          {data.own.filter((c) => c.ensemble_nature === "artist_estate").map((c) => (
            <EstatePiecesCard key={c.id} collectionId={c.id} collectionTitle={c.title || ""} />
          ))}
        </ol>
      )}
      <p style={{ marginTop: 24, fontSize: 12, color: "#8a8378", fontStyle: "italic", lineHeight: 1.5 }}>{L.disclaimer}</p>
    </div>
  );
}
